import React, { useEffect, useState, useCallback, useMemo } from "react";
import QRCode from "qrcode";
import { Link } from "react-router-dom";
import { http, currency, STATUS_ORDER, STATUS_CONFIG, API } from "../../lib/api";
import { useSettings } from "../../lib/SettingsContext";
import { generateOrderPDF } from "../../lib/orderPdf";
import { notifyWhatsApp, buildStatusMessage, normalizePhone } from "../../lib/whatsapp";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  Loader2, DollarSign, Package, Clock, CheckCircle2, X,
  Pencil, Plus, Minus, Trash2, Save, Ban, AlertTriangle, FileDown,
  Banknote, Landmark, CreditCard, MessageCircle, CalendarDays, Search, Monitor,
  ShieldCheck, ShieldAlert, ExternalLink, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { es } from "date-fns/locale";

const STATUS_TABS = [
  { value: "", label: "Todos" },
  ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_CONFIG[s].label })),
  { value: "cancelado", label: "Cancelado" },
];

const PAY_VIA_ICON = { transferencia: Landmark, efectivo: Banknote, stripe: CreditCard };
const PAY_VIA_LABEL = { transferencia: "Transferencia", efectivo: "Efectivo", stripe: "Stripe" };

// Convert a server-persisted item into an OrderItemInput-shaped draft the API accepts.
const itemToDraft = (it) => ({
  product_id: it.product_id,
  quantity: it.quantity,
  color_value: it.color_value || null,
  color_label: it.color_label || null,
  size_value: it.size_value || null,
  size_label: it.size_label || null,
  custom_width: it.custom_width ?? null,
  custom_height: it.custom_height ?? null,
  design_url: it.design_url || null,
  design_filename: it.design_filename || null,
  design_position: it.design_position || null,
  printing_method: it.printing_method || null,
  printing_method_label: it.printing_method_label || null,
  with_design_service: !!it.with_design_service,
  notes: it.notes || null,
  override_unit_price: it.override_unit_price ?? null,
  // UI-only fields (stripped before send)
  _product_name: it.product_name,
  _product_image: it.product_image,
  _line_total: it.line_total,
});

const stripUiFields = (d) => {
  const clean = { ...d };
  delete clean._product_name;
  delete clean._product_image;
  delete clean._line_total;
  // Normalize override: convert "" / undefined → null, and coerce number
  if (clean.override_unit_price === "" || clean.override_unit_price === undefined) {
    clean.override_unit_price = null;
  } else if (clean.override_unit_price !== null) {
    const n = Number(clean.override_unit_price);
    clean.override_unit_price = Number.isFinite(n) && n >= 0 ? n : null;
  }
  return clean;
};

export default function AdminOrders() {
  const { settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Item editing state
  const [editMode, setEditMode] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  // Ad-hoc quote items (not tied to catalog products)
  const [draftCustom, setDraftCustom] = useState([]);
  const [customName, setCustomName] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customQty, setCustomQty] = useState(1);
  const [customPrice, setCustomPrice] = useState("");
  const [savingItems, setSavingItems] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [pickerProductId, setPickerProductId] = useState("");
  const [pickerQty, setPickerQty] = useState(1);
  const [pickerColor, setPickerColor] = useState("");
  const [pickerSize, setPickerSize] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Quick filter for "deposit pending" — client-side toggle on top of statusFilter
  const [depositPendingOnly, setDepositPendingOnly] = useState(false);
  // Date range filter — client-side filter based on created_at
  const [dateRange, setDateRange] = useState(null); // { from: Date, to?: Date } | null
  // Text search across client name, phone and order ID
  const [searchTerm, setSearchTerm] = useState("");
  // If enabled, open WhatsApp automatically after each successful status change
  const [autoNotifyWA, setAutoNotifyWA] = useState(() => {
    try { return localStorage.getItem("printlab.autoNotifyWA") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("printlab.autoNotifyWA", autoNotifyWA ? "1" : "0"); } catch { /* ignore quota */ }
  }, [autoNotifyWA]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      http.get("/admin/stats"),
      http.get("/admin/orders", { params: statusFilter ? { status: statusFilter } : {} }),
    ])
      .then(([s, o]) => {
        setStats(s.data);
        setOrders(o.data);
      })
      .catch((err) => { console.warn("Failed to load orders/stats:", err?.message || err); })
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Deep-link support: when opening the admin with `?order_id=X` (e.g. from a QR scan)
  // auto-select that order once the list is loaded.
  useEffect(() => {
    if (!orders?.length) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get("order_id") || params.get("order_number");
    if (!targetId) return;
    const match = orders.find(
      (o) => o.order_id === targetId || String(o.order_number || "") === targetId
    );
    if (match) {
      setSelected(match);
      setEditMode(false);
      setDraftItems([]);
      setDraftCustom([]);
      // Clean the URL so a refresh doesn't keep reopening
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  // Fetch product catalog once (used by the "add item" picker)
  useEffect(() => {
    http.get("/admin/products").then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);

  const openOrder = (o) => {
    setSelected(o);
    setEditMode(false);
    setDraftItems([]);
    setDraftCustom([]);
  };
  const closeDrawer = () => {
    setSelected(null);
    setEditMode(false);
    setDraftItems([]);
    setDraftCustom([]);
  };

  const updateStatus = async (orderId, newStatus, message) => {
    setUpdating(true);
    try {
      const { data } = await http.put(`/admin/orders/${orderId}/status`, { status: newStatus, message });
      toast.success(`Pedido → ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      load();
      // Auto-notify by WhatsApp if enabled and phone available
      if (autoNotifyWA && data?.shipping?.phone) {
        const res = notifyWhatsApp(data, settings, newStatus);
        if (!res.ok) toast.warning(res.reason);
      }
    } catch {
      toast.error("No se pudo actualizar");
    } finally {
      setUpdating(false);
    }
  };

  const notifyCurrent = (order, statusOverride) => {
    if (!order) return;
    const res = notifyWhatsApp(order, settings, statusOverride);
    if (res.ok) {
      toast.success("Abriendo WhatsApp con el mensaje pre-armado");
      // Auto-log the notification so the admin has a follow-up timeline.
      logNotification(order.order_id, {
        type: statusOverride || order.status || "status",
        channel: "whatsapp",
      });
    } else {
      toast.error(res.reason || "No se pudo abrir WhatsApp");
    }
  };

  // ---- Notifications log (client follow-up control) ----
  const logNotification = async (orderId, { type, channel = "whatsapp", note = null, at = null } = {}) => {
    try {
      const { data } = await http.post(`/admin/orders/${orderId}/notifications`, {
        type,
        channel,
        note,
        at,
      });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      return data;
    } catch {
      toast.error("No se pudo registrar la notificación");
    }
  };

  const deleteNotification = async (orderId, index) => {
    try {
      const { data } = await http.delete(`/admin/orders/${orderId}/notifications/${index}`);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      toast.success("Registro eliminado");
    } catch {
      toast.error("No se pudo eliminar el registro");
    }
  };

  // ---- Project tag (internal control) ----
  const saveProject = async (orderId, projectText) => {
    try {
      const { data } = await http.put(`/admin/orders/${orderId}/project`, {
        project: projectText,
      });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      toast.success("Proyecto actualizado");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo actualizar el proyecto");
    }
  };

  // ---- Customer data edit (name/email/phone/address...) ----
  const saveCustomer = async (orderId, payload) => {
    try {
      const { data } = await http.put(`/admin/orders/${orderId}/customer`, payload);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      toast.success("Datos del cliente actualizados");
      return data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudieron actualizar los datos");
    }
  };

  // ---- Production done (internal, does NOT change status nor notify client) ----
  const toggleProductionDone = async (orderId, done) => {
    try {
      const { data } = await http.put(`/admin/orders/${orderId}/production-done`, { done });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      toast.success(done ? "Producción marcada como terminada" : "Producción reabierta");
    } catch {
      toast.error("No se pudo actualizar el estado interno de producción");
    }
  };

  // Manual total override (admin)
  const [manualTotalInput, setManualTotalInput] = useState("");
  const [savingTotal, setSavingTotal] = useState(false);
  useEffect(() => {
    // reset the input whenever the drawer opens a different order
    if (selected) setManualTotalInput(String(selected.total ?? ""));
    else setManualTotalInput("");
  }, [selected?.order_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveManualTotal = async () => {
    if (!selected) return;
    const val = parseFloat(manualTotalInput);
    if (isNaN(val) || val < 0) {
      toast.error("Ingresa un total válido (>= 0)");
      return;
    }
    setSavingTotal(true);
    try {
      const { data } = await http.put(`/admin/orders/${selected.order_id}/total`, { total: val });
      setOrders((prev) => prev.map((o) => (o.order_id === selected.order_id ? data : o)));
      setSelected(data);
      toast.success("Total del pedido actualizado");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo actualizar el total");
    } finally {
      setSavingTotal(false);
    }
  };

  const clearManualTotal = async () => {
    if (!selected) return;
    setSavingTotal(true);
    try {
      const { data } = await http.put(`/admin/orders/${selected.order_id}/total`, { total: null });
      setOrders((prev) => prev.map((o) => (o.order_id === selected.order_id ? data : o)));
      setSelected(data);
      toast.success("Override eliminado — total recalculado");
    } catch {
      toast.error("No se pudo eliminar el override");
    } finally {
      setSavingTotal(false);
    }
  };

  const toggleIva = async (enabled) => {
    if (!selected) return;
    try {
      const { data } = await http.put(`/admin/orders/${selected.order_id}/iva`, { enabled });
      setOrders((prev) => prev.map((o) => (o.order_id === selected.order_id ? data : o)));
      setSelected(data);
      toast.success(enabled ? "IVA agregado al pedido (16%)" : "IVA eliminado del pedido");
    } catch {
      toast.error("No se pudo actualizar el IVA");
    }
  };

  // Payment tracking (deposit + final) — admin can flip flags and record the received via
  const updatePayment = async (orderId, patch) => {
    try {
      const { data } = await http.put(`/admin/orders/${orderId}/payment`, patch);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      toast.success("Pagos actualizados");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo actualizar el pago");
    }
  };

  // Delivery estimate — admin can manually override the auto-computed date
  const updateDeliveryEstimate = async (orderId, isoDate) => {
    try {
      const { data } = await http.put(`/admin/orders/${orderId}/delivery-estimate`, { delivery_estimate: isoDate });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      if (selected?.order_id === orderId) setSelected(data);
      toast.success("Fecha de entrega actualizada");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo actualizar la fecha");
    }
  };

  const startEditItems = () => {
    setDraftItems((selected.items || []).map(itemToDraft));
    setDraftCustom(
      (selected.custom_items || []).map((c) => ({
        name: c.name || "",
        description: c.description || "",
        quantity: Number(c.quantity) || 1,
        unit_price: Number(c.unit_price) || 0,
      }))
    );
    setCustomName("");
    setCustomDescription("");
    setCustomQty(1);
    setCustomPrice("");
    setPickerProductId("");
    setPickerQty(1);
    setPickerColor("");
    setPickerSize("");
    setEditMode(true);
  };
  const cancelEditItems = () => {
    setEditMode(false);
    setDraftItems([]);
    setDraftCustom([]);
  };

  // ---- Custom (ad-hoc) item helpers ----
  const addDraftCustom = () => {
    const name = customName.trim();
    const qty = Math.max(1, parseInt(customQty || 1, 10));
    const price = parseFloat(customPrice);
    if (!name) {
      toast.error("Ingresa la descripción/nombre del producto");
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Ingresa un costo unitario válido (>= 0)");
      return;
    }
    setDraftCustom((arr) => [
      ...arr,
      { name, description: customDescription.trim() || null, quantity: qty, unit_price: price },
    ]);
    setCustomName("");
    setCustomDescription("");
    setCustomQty(1);
    setCustomPrice("");
  };
  const removeDraftCustom = (i) => {
    setDraftCustom((arr) => arr.filter((_, idx) => idx !== i));
  };
  const setDraftCustomField = (i, field, value) => {
    setDraftCustom((arr) =>
      arr.map((d, idx) => {
        if (idx !== i) return d;
        if (field === "quantity") return { ...d, quantity: Math.max(1, parseInt(value || 1, 10)) };
        if (field === "unit_price") {
          const n = parseFloat(value);
          return { ...d, unit_price: Number.isFinite(n) && n >= 0 ? n : 0 };
        }
        return { ...d, [field]: value };
      })
    );
  };

  const changeDraftQty = (i, delta) => {
    setDraftItems((arr) =>
      arr.map((d, idx) => (idx === i ? { ...d, quantity: Math.max(1, (d.quantity || 1) + delta) } : d))
    );
  };
  const setDraftQty = (i, v) => {
    const n = Math.max(1, parseInt(v || 1, 10));
    setDraftItems((arr) => arr.map((d, idx) => (idx === i ? { ...d, quantity: n } : d)));
  };
  const removeDraftItem = (i) => {
    setDraftItems((arr) => arr.filter((_, idx) => idx !== i));
  };
  const setDraftOverride = (i, v) => {
    setDraftItems((arr) =>
      arr.map((d, idx) => {
        if (idx !== i) return d;
        const raw = v === "" || v === null ? null : Number(v);
        const override = raw !== null && Number.isFinite(raw) && raw >= 0 ? raw : null;
        // Rough preview — server recomputes precisely with design/extras fees
        const preview = override !== null ? override * (d.quantity || 1) : (d._line_total || 0);
        return { ...d, override_unit_price: override, _line_total: preview };
      })
    );
  };

  const pickerProduct = useMemo(
    () => allProducts.find((p) => p.product_id === pickerProductId),
    [allProducts, pickerProductId]
  );

  useEffect(() => {
    // Reset variant when product changes
    if (pickerProduct) {
      setPickerColor(pickerProduct.colors?.[0]?.value || "");
      setPickerSize(pickerProduct.sizes?.[0]?.value || "");
      // Admin can start at 1 — the product's public min_qty is only shown as info.
      setPickerQty(1);
    }
  }, [pickerProduct]);

  const addDraftItemFromPicker = () => {
    if (!pickerProduct) {
      toast.error("Selecciona un producto");
      return;
    }
    if (pickerQty < 1) {
      toast.error("La cantidad debe ser al menos 1");
      return;
    }
    const color = pickerProduct.colors?.find((c) => c.value === pickerColor) || null;
    const size = pickerProduct.sizes?.find((s) => s.value === pickerSize) || null;
    const unit =
      Number(pickerProduct.base_price) +
      Number(color?.extra_price || 0) +
      Number(size?.extra_price || 0);
    setDraftItems((arr) => [
      ...arr,
      {
        product_id: pickerProduct.product_id,
        quantity: pickerQty,
        color_value: color?.value || null,
        color_label: color?.label || null,
        size_value: size?.value || null,
        size_label: size?.label || null,
        custom_width: null,
        custom_height: null,
        design_url: null,
        design_filename: null,
        design_position: null,
        printing_method: null,
        printing_method_label: null,
        with_design_service: false,
        notes: null,
        _product_name: pickerProduct.name,
        _product_image: pickerProduct.base_image_url,
        _line_total: unit * pickerQty, // preview only; server recomputes with bulk rules
      },
    ]);
    setPickerProductId("");
    setPickerQty(1);
  };

  const saveItems = async () => {
    if (draftItems.length === 0 && draftCustom.length === 0) {
      toast.error("Agrega al menos un artículo (regular o personalizado) para guardar.");
      return;
    }
    setSavingItems(true);
    try {
      const payload = {
        items: draftItems.map(stripUiFields),
        custom_items: draftCustom.map((c) => ({
          name: c.name,
          description: c.description || null,
          quantity: c.quantity,
          unit_price: c.unit_price,
        })),
      };
      const { data } = await http.put(`/admin/orders/${selected.order_id}/items`, payload);
      toast.success("Pedido actualizado");
      setSelected(data);
      setOrders((prev) => prev.map((o) => (o.order_id === data.order_id ? data : o)));
      setEditMode(false);
      setDraftItems([]);
      setDraftCustom([]);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo actualizar el pedido");
    } finally {
      setSavingItems(false);
    }
  };

  const deleteOrder = async () => {
    if (!window.confirm(`¿Eliminar definitivamente el pedido ${selected.order_id}? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await http.delete(`/admin/orders/${selected.order_id}`);
      toast.success("Pedido eliminado");
      setOrders((prev) => prev.filter((o) => o.order_id !== selected.order_id));
      closeDrawer();
      load();
    } catch {
      toast.error("No se pudo eliminar el pedido");
    } finally {
      setDeleting(false);
    }
  };

  // (previewTotals defined above)

  // Helper — convert Date to local YYYY-MM-DD (avoids UTC drift)
  const fmtDayKey = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  // Orders visible after applying quick filter
  const visibleOrders = useMemo(() => {
    let list = orders;
    if (depositPendingOnly) {
      list = list.filter((o) => !o.deposit_paid && !["cancelado", "entregado"].includes(o.status));
    }
    if (dateRange?.from) {
      const fromKey = fmtDayKey(dateRange.from);
      const toKey = fmtDayKey(dateRange.to || dateRange.from);
      list = list.filter((o) => {
        const key = (o.created_at || "").slice(0, 10);
        return key >= fromKey && key <= toKey;
      });
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      // Normalize digits — allows searching by phone with or without spaces / prefix
      const qDigits = q.replace(/\D/g, "");
      list = list.filter((o) => {
        const name = (o.shipping?.name || o.user_name || "").toLowerCase();
        const phoneDigits = (o.shipping?.phone || "").replace(/\D/g, "");
        const email = (o.user_email || o.guest_email || "").toLowerCase();
        const oid = (o.order_id || "").toLowerCase();
        const oNum = String(o.order_number || "");
        if (name.includes(q)) return true;
        if (email.includes(q)) return true;
        if (oid.includes(q)) return true;
        if (oNum && oNum.includes(qDigits || q)) return true;
        if (qDigits && phoneDigits.includes(qDigits)) return true;
        return false;
      });
    }
    return list;
  }, [orders, depositPendingOnly, dateRange, searchTerm]);

  // Totals of the visible list — useful for period reports
  const visibleTotals = useMemo(() => {
    const revenue = visibleOrders.reduce((acc, o) => acc + Number(o.total || 0), 0);
    const units = visibleOrders.reduce((acc, o) => acc + (o.items || []).reduce((a, i) => a + Number(i.quantity || 0), 0), 0);
    return { orders: visibleOrders.length, revenue, units };
  }, [visibleOrders]);

  // Dates with at least one order — used to highlight the calendar filter
  const orderDates = useMemo(() => {
    const set = new Set();
    for (const o of orders) {
      const iso = o.created_at || "";
      if (iso.length >= 10) set.add(iso.slice(0, 10));
    }
    return Array.from(set).map((key) => {
      const [y, m, d] = key.split("-").map(Number);
      return new Date(y, m - 1, d);
    });
  }, [orders]);

  // Quick preset ranges
  const applyPreset = (kind) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (kind === "today") {
      setDateRange({ from: today, to: today });
    } else if (kind === "last7") {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      setDateRange({ from, to: today });
    } else if (kind === "last30") {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      setDateRange({ from, to: today });
    } else if (kind === "month") {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateRange({ from, to: today });
    } else if (kind === "prevMonth") {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      setDateRange({ from, to });
    }
  };

  const rangeLabel = useMemo(() => {
    if (!dateRange?.from) return "Filtrar por fecha";
    const opts = { day: "numeric", month: "short" };
    const fromStr = dateRange.from.toLocaleDateString("es-MX", opts);
    const toDate = dateRange.to || dateRange.from;
    // Same day?
    if (fmtDayKey(dateRange.from) === fmtDayKey(toDate)) {
      return dateRange.from.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
    }
    const toStr = toDate.toLocaleDateString("es-MX", { ...opts, year: "numeric" });
    return `${fromStr} – ${toStr}`;
  }, [dateRange]);

  // Estimated preview totals during edit — uses live settings, server recomputes on save
  const previewTotals = useMemo(() => {
    const subReg = draftItems.reduce((acc, d) => acc + (d._line_total || 0), 0);
    const subCust = draftCustom.reduce(
      (acc, d) => acc + Number(d.unit_price || 0) * Number(d.quantity || 0),
      0,
    );
    const sub = subReg + subCust;
    const fee = Number(settings?.shipping_fee_domicilio ?? 99);
    const threshold = Number(settings?.shipping_free_threshold ?? 0);
    let ship = 0;
    if (sub > 0 && selected?.delivery_method !== "sucursal") {
      ship = (threshold > 0 && sub >= threshold) ? 0 : fee;
    }
    return { subtotal: sub, shipping: ship, total: sub + ship };
  }, [draftItems, draftCustom, selected, settings]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Pedidos totales", value: stats?.total_orders ?? "—", icon: Package },
          { label: "Pendientes", value: stats?.pending ?? "—", icon: Clock, color: "#F59E0B" },
          { label: "En producción", value: stats?.in_production ?? "—", icon: Package, color: "#EC4899" },
          { label: "Entregados", value: stats?.delivered ?? "—", icon: CheckCircle2, color: "#22C55E" },
          { label: "Ingresos", value: stats ? currency(stats.revenue) : "—", icon: DollarSign, color: "#10B981" },
        ].map((s) => (
          <div key={s.label} className="brutal-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase text-neutral-500">{s.label}</div>
              <s.icon className="w-4 h-4" style={{ color: s.color || "#0A0A0A" }} />
            </div>
            <div className="font-display text-3xl mt-2 tracking-tight">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Search + Quick filters + Production Board */}
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/admin/produccion"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 px-4 h-10 border-2 border-black bg-black text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
          data-testid="admin-open-production-board"
          title="Abrir tablero de producción en pantalla completa (nueva pestaña)"
        >
          <Monitor className="w-4 h-4" />
          Tablero de Producción
        </Link>
        <div className="relative flex-1 min-w-[220px] max-w-md" data-testid="admin-search-wrapper">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar por cliente, teléfono o ID…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-10 pl-9 pr-9 border border-neutral-300 bg-white text-sm outline-none focus:border-black"
            data-testid="admin-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 text-neutral-500"
              aria-label="Limpiar búsqueda"
              data-testid="admin-search-clear"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        <button
          onClick={() => setDepositPendingOnly((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2 border-2 transition-colors text-sm font-medium ${
            depositPendingOnly
              ? "bg-amber-500 border-amber-500 text-white"
              : "border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100"
          }`}
          data-testid="admin-filter-deposit-pending"
        >
          <Banknote className="w-4 h-4" />
          Anticipo pendiente
          {typeof stats?.deposit_pending === "number" && (
            <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-mono rounded-sm ${depositPendingOnly ? "bg-white text-amber-700" : "bg-amber-500 text-white"}`}>
              {stats.deposit_pending}
            </span>
          )}
        </button>

        {/* Date range filter */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`flex items-center gap-2 px-4 py-2 border-2 transition-colors text-sm font-medium ${
                dateRange?.from
                  ? "bg-black border-black text-white"
                  : "border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50"
              }`}
              data-testid="admin-filter-date-btn"
            >
              <CalendarDays className="w-4 h-4" />
              {rangeLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-auto" align="start">
            {/* Presets */}
            <div className="flex flex-wrap gap-1 p-2 border-b border-neutral-200 bg-neutral-50">
              {[
                { k: "today", label: "Hoy" },
                { k: "last7", label: "7 días" },
                { k: "last30", label: "30 días" },
                { k: "month", label: "Este mes" },
                { k: "prevMonth", label: "Mes anterior" },
              ].map((p) => (
                <button
                  key={p.k}
                  type="button"
                  onClick={() => applyPreset(p.k)}
                  className="px-2 py-1 text-[11px] font-mono uppercase border border-neutral-200 bg-white hover:bg-black hover:text-white hover:border-black transition-colors"
                  data-testid={`admin-filter-preset-${p.k}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Calendar
              mode="range"
              locale={es}
              numberOfMonths={2}
              selected={dateRange || undefined}
              onSelect={(r) => setDateRange(r || null)}
              defaultMonth={dateRange?.from || new Date()}
              modifiers={{ hasOrder: orderDates }}
              modifiersClassNames={{ hasOrder: "font-semibold underline decoration-emerald-500 decoration-2 underline-offset-4" }}
              disabled={{ after: new Date() }}
              data-testid="admin-filter-date-calendar"
            />
            <div className="border-t border-neutral-200 p-2 flex items-center justify-between text-[11px] text-neutral-500">
              <span className="font-mono">
                <span className="underline decoration-emerald-500 decoration-2 underline-offset-4 font-semibold text-neutral-700">Días</span> con pedidos
              </span>
              {dateRange?.from && (
                <button
                  type="button"
                  onClick={() => setDateRange(null)}
                  className="text-xs text-red-600 hover:underline font-medium"
                  data-testid="admin-filter-date-clear"
                >
                  Limpiar filtro
                </button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {(depositPendingOnly || dateRange?.from) && (
          <span className="text-xs text-neutral-500 font-mono">
            {depositPendingOnly && "Solo sin anticipo"}
            {depositPendingOnly && dateRange?.from && " · "}
            {dateRange?.from && `Rango: ${rangeLabel}`}
          </span>
        )}
      </div>

      {/* Period totals — visible only when a range is applied */}
      {dateRange?.from && (
        <div className="brutal-card p-4 bg-neutral-50 border-l-4 border-l-black" data-testid="admin-range-summary">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-mono uppercase text-neutral-600">
              Resumen del periodo · {rangeLabel}
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-[10px] font-mono uppercase text-neutral-500">Pedidos</div>
                <div className="font-display text-2xl tracking-tight" data-testid="range-summary-orders">{visibleTotals.orders}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-neutral-500">Piezas</div>
                <div className="font-display text-2xl tracking-tight">{visibleTotals.units}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase text-neutral-500">Ingresos</div>
                <div className="font-display text-2xl tracking-tight text-emerald-700" data-testid="range-summary-revenue">
                  {currency(visibleTotals.revenue)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 border border-neutral-200 bg-white p-1 overflow-auto">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value || "all"}
            onClick={() => setStatusFilter(t.value)}
            className={`px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
              statusFilter === t.value ? "bg-black text-white" : "hover:bg-neutral-100"
            }`}
            data-testid={`admin-filter-${t.value || "all"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="brutal-card overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] min-h-[400px]" data-testid="admin-orders-table-scroll">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
        ) : visibleOrders.length === 0 ? (
          <div className="p-12 text-center text-neutral-500" data-testid="admin-orders-empty">
            {searchTerm
              ? `No hay resultados para "${searchTerm}". Intenta con otro nombre, teléfono o ID.`
              : dateRange?.from
              ? `No hay pedidos en el rango seleccionado (${rangeLabel}).`
              : depositPendingOnly
              ? "No hay pedidos con anticipo pendiente. ¡Excelente!"
              : "No hay pedidos"}
          </div>
        ) : (
          <table className="w-full text-sm min-w-[1500px]">
            <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
              <tr className="border-b border-neutral-200 text-left">
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Pedido</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">N°</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Cliente</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Proyecto</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Items</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Total / Saldo</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-center whitespace-nowrap">Anticipo</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-center whitespace-nowrap">Finiquito</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Vía</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-center whitespace-nowrap">Producción</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap">Estatus</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((o) => {
                const viaCode = o.deposit_paid_via || o.final_paid_via || o.payment_method;
                const ViaIcon = PAY_VIA_ICON[viaCode] || CreditCard;
                const statusColor = STATUS_CONFIG[o.status]?.color || "#9CA3AF";
                return (
                  <tr
                    key={o.order_id}
                    className="border-b border-neutral-100 transition-colors"
                    style={{
                      borderLeft: `8px solid ${statusColor}`,
                      backgroundColor: `${statusColor}20`, // ~12% tint
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${statusColor}33`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${statusColor}20`; }}
                    data-testid={`admin-order-row-${o.order_id}`}
                  >
                    <td className="p-3 align-top">
                      <div className="font-mono text-[11px] text-neutral-600 break-all max-w-[130px]" data-testid={`admin-row-id-${o.order_id}`} title={o.order_id}>
                        {o.order_id}
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      {o.order_number ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <div className="font-display text-lg leading-none" data-testid={`admin-row-number-${o.order_id}`}>#{o.order_number}</div>
                          {o.customer_approved ? (
                            <span
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-emerald-600 text-white text-[8px] font-mono uppercase rounded-sm"
                              title={`Autorizado por el cliente el ${new Date(o.customer_approved_at).toLocaleString("es-MX", { timeZone: "America/Monterrey" })}`}
                              data-testid={`admin-row-approved-${o.order_id}`}
                            >
                              <ShieldCheck className="w-2.5 h-2.5" /> AUT
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-amber-500 text-white text-[8px] font-mono uppercase rounded-sm"
                              title="El cliente aún NO ha autorizado el pedido"
                              data-testid={`admin-row-unapproved-${o.order_id}`}
                            >
                              <ShieldAlert className="w-2.5 h-2.5" /> S/AUT
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-neutral-400 italic">—</span>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      <div className="font-medium" data-testid={`admin-row-name-${o.order_id}`}>
                        {o.user_name || o.shipping?.name || "—"}
                      </div>
                      <div className="text-xs text-neutral-500" data-testid={`admin-row-email-${o.order_id}`}>
                        {o.user_email || <span className="italic text-neutral-400">Sin correo</span>}
                      </div>
                      {o.shipping?.phone && (
                        <div className="text-[10px] font-mono text-neutral-400 mt-0.5">
                          📱 {o.shipping.phone}
                        </div>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      <ProjectInline
                        order={o}
                        onSave={(v) => saveProject(o.order_id, v)}
                      />
                    </td>
                    <td className="p-3 align-top">
                      <ItemsSummary order={o} />
                    </td>
                    <td className="p-3 align-top">
                      {(() => {
                        const dep = Number(o.deposit_amount ?? o.total * 0.5);
                        const paid = (o.deposit_paid ? dep : 0) + (o.final_paid ? (o.total - dep) : 0);
                        const balance = Math.max(0, (o.total || 0) - paid);
                        return (
                          <div>
                            <div className="font-medium" data-testid={`admin-row-total-${o.order_id}`}>
                              {currency(o.total)}
                            </div>
                            {balance > 0 ? (
                              <div
                                className="text-[10px] font-mono text-[#FF3B30] mt-0.5"
                                data-testid={`admin-row-balance-${o.order_id}`}
                                title={`Cobrado ${currency(paid)} · falta ${currency(balance)}`}
                              >
                                Saldo: {currency(balance)}
                              </div>
                            ) : (
                              <div className="text-[10px] font-mono text-emerald-700 mt-0.5" data-testid={`admin-row-balance-${o.order_id}`}>
                                Pagado ✓
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3 text-center align-top">
                      <div className="flex flex-col items-center gap-0.5">
                        <input
                          type="checkbox"
                          checked={!!o.deposit_paid}
                          onChange={(e) => updatePayment(o.order_id, {
                            deposit_paid: e.target.checked,
                            deposit_paid_via: e.target.checked ? (o.deposit_paid_via || o.payment_method || "transferencia") : null,
                          })}
                          className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          data-testid={`admin-row-deposit-${o.order_id}`}
                          title={`Anticipo ${currency(o.deposit_amount ?? o.total * 0.5)}`}
                        />
                        <span className="text-[9px] font-mono text-neutral-500">
                          {currency(o.deposit_amount ?? o.total * 0.5)}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-center align-top">
                      <div className="flex flex-col items-center gap-0.5">
                        <input
                          type="checkbox"
                          checked={!!o.final_paid}
                          onChange={(e) => updatePayment(o.order_id, {
                            final_paid: e.target.checked,
                            final_paid_via: e.target.checked ? (o.final_paid_via || o.payment_method || "efectivo") : null,
                          })}
                          className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          data-testid={`admin-row-final-${o.order_id}`}
                          title="Finiquito restante"
                        />
                        <span className="text-[9px] font-mono text-neutral-500">
                          {currency((o.total || 0) - Number(o.deposit_amount ?? o.total * 0.5))}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex flex-col gap-0.5 text-[10px] font-mono uppercase">
                        <span className="flex items-center gap-1 text-neutral-600">
                          <ViaIcon className="w-3 h-3" />
                          {PAY_VIA_LABEL[viaCode] || viaCode}
                        </span>
                        <span
                          className={`px-1.5 py-0.5 border text-center ${
                            o.payment_status === "paid"
                              ? "border-emerald-500 text-emerald-700"
                              : o.payment_status === "partial"
                                ? "border-amber-500 text-amber-700"
                                : "border-neutral-300 text-neutral-500"
                          }`}
                          data-testid={`admin-row-paystatus-${o.order_id}`}
                        >
                          {o.payment_status === "paid" ? "Pagado" : o.payment_status === "partial" ? "Parcial" : "Pendiente"}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-center align-top">
                      <label
                        className="inline-flex flex-col items-center gap-0.5 cursor-pointer"
                        title={
                          o.production_done && o.production_done_at
                            ? `Terminado ${new Date(o.production_done_at).toLocaleString("es-MX", { timeZone: "America/Monterrey" })}${o.production_done_by ? " · " + o.production_done_by : ""}`
                            : "Marcar producción como terminada (control interno, no notifica al cliente)"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={!!o.production_done}
                          onChange={(e) => toggleProductionDone(o.order_id, e.target.checked)}
                          className="w-5 h-5 accent-emerald-600 cursor-pointer"
                          data-testid={`admin-row-prod-done-${o.order_id}`}
                        />
                        {o.production_done && (
                          <span className="text-[9px] font-mono text-emerald-700 uppercase">Lista</span>
                        )}
                      </label>
                    </td>
                    <td className="p-3 align-top">
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="p-3 text-right align-top">
                      <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => openOrder(o)} data-testid={`admin-manage-${o.order_id}`}>
                        Gestionar
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="admin-drawer">
          <div className="flex-1 bg-black/40" onClick={closeDrawer} />
          <div className="w-full max-w-xl bg-white h-full overflow-auto border-l border-neutral-200">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between z-10">
              <div>
                <div className="text-xs font-mono uppercase text-neutral-500">Pedido</div>
                <div className="font-display text-2xl leading-tight">
                  {selected.order_number ? `#${selected.order_number}` : selected.order_id}
                </div>
                {selected.order_number && (
                  <div className="text-[9px] font-mono text-neutral-400 truncate max-w-[220px]" title={selected.order_id}>
                    {selected.order_id}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selected.order_number && (
                  <a
                    href={`/mi-pedido/${selected.order_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 h-9 px-3 border-2 border-black bg-white text-xs font-mono uppercase hover:bg-black hover:text-white transition-colors"
                    data-testid="admin-open-customer-panel"
                    title="Abrir el panel que ve el cliente (vista pública)"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Panel cliente
                  </a>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none h-9"
                  onClick={async () => {
                    try {
                      await generateOrderPDF(selected, settings || {});
                      toast.success("PDF generado");
                    } catch (err) {
                      toast.error("No se pudo generar el PDF");
                    }
                  }}
                  data-testid="admin-order-download-pdf"
                >
                  <FileDown className="w-4 h-4 mr-1.5" /> PDF
                </Button>
                <button onClick={closeDrawer} className="p-2 hover:bg-neutral-100" data-testid="admin-drawer-close"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <OrderQRPanel order={selected} />
              <AdminDesignsBlock
                order={selected}
                onUpdate={(data) => { setSelected(data); load(); }}
              />
              {Array.isArray(selected.customer_attachments) && selected.customer_attachments.length > 0 && (
                <div className="border-2 border-blue-500 bg-blue-50 p-3" data-testid="admin-drawer-customer-attachments">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-mono uppercase text-blue-900 font-bold">Adjuntos del cliente ({selected.customer_attachments.length})</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {selected.customer_attachments.map((att, i) => {
                      const isImg = (att.content_type || "").startsWith("image/");
                      const url = `${API}/files/${att.storage_path}`;
                      return (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group border-2 border-blue-300 bg-white p-2 hover:border-blue-600 transition-colors"
                          data-testid={`admin-customer-attachment-${i}`}
                        >
                          <div className="w-full h-24 bg-neutral-100 flex items-center justify-center overflow-hidden">
                            {isImg ? (
                              <img src={url} alt={att.filename} className="max-w-full max-h-full object-contain" />
                            ) : (
                              <FileDown className="w-8 h-8 text-neutral-400" />
                            )}
                          </div>
                          <div className="mt-1 text-[10px] font-mono truncate" title={att.filename}>{att.filename}</div>
                          {att.slot && <div className="text-[9px] font-mono text-blue-700">Caja {att.slot}</div>}
                          {att.measurements && (
                            <div className="text-[10px] text-neutral-700 mt-0.5 whitespace-pre-wrap"><span className="font-mono text-blue-700">Medidas:</span> {att.measurements}</div>
                          )}
                          {att.note && <div className="text-[10px] text-neutral-500 mt-0.5 italic">"{att.note}"</div>}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono uppercase text-neutral-500">Cambiar estatus</div>
                  <label className="flex items-center gap-1.5 text-[11px] font-mono text-neutral-600 cursor-pointer" data-testid="admin-auto-notify-toggle-label">
                    <input
                      type="checkbox"
                      checked={autoNotifyWA}
                      onChange={(e) => setAutoNotifyWA(e.target.checked)}
                      className="w-3.5 h-3.5 accent-emerald-600"
                      data-testid="admin-auto-notify-toggle"
                    />
                    Auto-notificar WhatsApp
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_ORDER.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.order_id, s)}
                      disabled={updating || selected.status === s}
                      className={`p-2 border text-xs font-medium text-left transition-colors ${
                        selected.status === s ? "bg-black text-white border-black" : "border-neutral-200 hover:border-black"
                      }`}
                      data-testid={`admin-set-status-${s}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="status-dot" style={{ background: STATUS_CONFIG[s].color }} />
                        {STATUS_CONFIG[s].label}
                      </div>
                    </button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="rounded-none w-full mt-2 border-red-500 text-red-600 hover:bg-red-50"
                  onClick={() => updateStatus(selected.order_id, "cancelado", "Pedido cancelado")}
                  disabled={updating || selected.status === "cancelado"}
                  data-testid="admin-cancel-order"
                >
                  Cancelar pedido
                </Button>
                {/* WhatsApp notification block */}
                <div className="mt-3 border-2 border-emerald-500/60 bg-emerald-50/60 p-3" data-testid="admin-wa-notify-block">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-emerald-700" />
                    <div className="font-medium text-sm">Notificar al cliente por WhatsApp</div>
                  </div>
                  {selected.shipping?.phone ? (
                    <>
                      <Button
                        onClick={() => notifyCurrent(selected)}
                        className="w-full rounded-none h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="admin-notify-whatsapp"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Enviar WhatsApp al cliente ({normalizePhone(selected.shipping.phone)})
                      </Button>
                      <div className="mt-2 text-[11px] text-neutral-700 leading-snug italic border-l-2 border-emerald-400 pl-2" data-testid="admin-wa-preview">
                        {buildStatusMessage(selected, settings)}
                      </div>
                      <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                        Abre WhatsApp Web/App con el mensaje pre-armado. Solo das click en Enviar.
                      </div>

                      {/* Manual outreach: follow-up + Google review */}
                      <div className="mt-3 pt-3 border-t border-emerald-300/60 grid grid-cols-1 gap-2">
                        <Button
                          onClick={() => notifyCurrent(selected, "follow_up")}
                          variant="outline"
                          className="w-full rounded-none h-9 border-emerald-600 text-emerald-700 hover:bg-emerald-100 text-xs"
                          data-testid="admin-wa-followup"
                        >
                          <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                          Seguimiento de la propuesta
                        </Button>
                        {settings?.google_review_url ? (
                          <Button
                            onClick={() => notifyCurrent(selected, "review")}
                            variant="outline"
                            className="w-full rounded-none h-9 border-amber-500 text-amber-700 hover:bg-amber-50 text-xs"
                            data-testid="admin-wa-review"
                          >
                            <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
                            Solicitar reseña de Google
                          </Button>
                        ) : (
                          <div className="text-[10px] text-neutral-500 italic">Añade el link de reseña de Google en Ajustes para habilitar el botón de reseña.</div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-neutral-500 italic">Este pedido no tiene número de WhatsApp del cliente.</div>
                  )}

                  {/* Notifications history — follow-up control */}
                  <NotificationsLog
                    order={selected}
                    onDelete={(idx) => deleteNotification(selected.order_id, idx)}
                    onLogManual={(entry) => logNotification(selected.order_id, entry)}
                  />
                </div>
              </div>

              {/* Proyecto (internal tag) */}
              <div className="border border-neutral-200 p-3" data-testid="admin-project-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-neutral-700" />
                  <div className="font-medium text-sm">Proyecto (control interno)</div>
                </div>
                <ProjectDrawerField
                  order={selected}
                  onSave={(v) => saveProject(selected.order_id, v)}
                />
              </div>

              {/* Delivery estimate (manual override) */}
              <div className="border border-neutral-200 p-3" data-testid="admin-delivery-panel">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="w-4 h-4 text-neutral-700" />
                  <div className="font-medium text-sm">Fecha de entrega estimada</div>
                  {selected.delivery_estimate_manual && (
                    <span className="text-[9px] font-mono uppercase bg-amber-100 text-amber-800 border border-amber-300 px-1 py-0.5">
                      Manual
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    key={`del-${selected.order_id}-${selected.delivery_estimate}`}
                    type="date"
                    defaultValue={selected.delivery_estimate || ""}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val && val !== selected.delivery_estimate) {
                        updateDeliveryEstimate(selected.order_id, val);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.target.blur(); }
                      if (e.key === "Escape") { e.target.value = selected.delivery_estimate || ""; e.target.blur(); }
                    }}
                    className="border border-neutral-300 h-9 px-2 text-sm font-mono outline-none focus:border-black"
                    data-testid="admin-delivery-input"
                  />
                  <span className="text-[10px] text-neutral-500">Al cambiar la fecha se registra en el historial del pedido.</span>
                </div>
              </div>

              {/* Payments panel */}
              <div className="border-2 border-emerald-500/50 bg-emerald-50/30 p-4" data-testid="admin-payments-panel">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-emerald-700" />
                  <div className="font-medium text-sm">Pagos recibidos</div>
                </div>

                {/* Deposit row — editable amount */}
                <div className="grid grid-cols-12 gap-2 items-center mb-2">
                  <label className="col-span-5 flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selected.deposit_paid}
                      onChange={(e) => updatePayment(selected.order_id, {
                        deposit_paid: e.target.checked,
                        deposit_paid_via: e.target.checked ? (selected.deposit_paid_via || selected.payment_method || "transferencia") : null,
                      })}
                      className="w-4 h-4 accent-emerald-600"
                      data-testid="admin-drawer-deposit-check"
                    />
                    <span className="font-medium">Anticipo</span>
                  </label>
                  <div className="col-span-4 relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-mono text-neutral-500">$</span>
                    <input
                      key={`dep-${selected.order_id}-${selected.deposit_amount}`}
                      type="number"
                      step="0.01"
                      min="0"
                      max={selected.total || undefined}
                      defaultValue={Number(selected.deposit_amount ?? selected.total * 0.5).toFixed(2)}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        const current = Number(selected.deposit_amount ?? selected.total * 0.5);
                        if (!isNaN(val) && Math.abs(val - current) > 0.009) {
                          updatePayment(selected.order_id, { deposit_amount: val });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.target.blur(); }
                        if (e.key === "Escape") { e.target.value = Number(selected.deposit_amount ?? selected.total * 0.5).toFixed(2); e.target.blur(); }
                      }}
                      className="w-full border border-neutral-300 rounded-none pl-5 pr-1 h-8 text-sm font-mono text-right focus:outline-none focus:border-black"
                      data-testid="admin-drawer-deposit-amount"
                      title="Monto exacto del anticipo recibido"
                    />
                  </div>
                  <select
                    className="col-span-3 border border-neutral-200 rounded-none px-1 h-8 text-xs bg-white disabled:opacity-50"
                    value={selected.deposit_paid_via || ""}
                    disabled={!selected.deposit_paid}
                    onChange={(e) => updatePayment(selected.order_id, { deposit_paid_via: e.target.value })}
                    data-testid="admin-drawer-deposit-via"
                  >
                    <option value="">Vía…</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </div>

                {/* Final row — amount auto-calculated as (total - deposit) */}
                <div className="grid grid-cols-12 gap-2 items-center">
                  <label className="col-span-5 flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selected.final_paid}
                      onChange={(e) => updatePayment(selected.order_id, {
                        final_paid: e.target.checked,
                        final_paid_via: e.target.checked ? (selected.final_paid_via || selected.payment_method || "efectivo") : null,
                      })}
                      className="w-4 h-4 accent-emerald-600"
                      data-testid="admin-drawer-final-check"
                    />
                    <span className="font-medium">Finiquito</span>
                  </label>
                  <div className="col-span-4 text-sm font-mono text-neutral-700 text-right pr-1" data-testid="admin-drawer-final-amount">
                    {currency((selected.total || 0) - (selected.deposit_amount ?? selected.total * 0.5))}
                  </div>
                  <select
                    className="col-span-3 border border-neutral-200 rounded-none px-1 h-8 text-xs bg-white disabled:opacity-50"
                    value={selected.final_paid_via || ""}
                    disabled={!selected.final_paid}
                    onChange={(e) => updatePayment(selected.order_id, { final_paid_via: e.target.value })}
                    data-testid="admin-drawer-final-via"
                  >
                    <option value="">Vía…</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </div>

                {/* Balance summary */}
                {(() => {
                  const dep = Number(selected.deposit_amount ?? selected.total * 0.5);
                  const paid = (selected.deposit_paid ? dep : 0) + (selected.final_paid ? (selected.total - dep) : 0);
                  const balance = Math.max(0, (selected.total || 0) - paid);
                  return (
                    <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-emerald-200" data-testid="admin-drawer-balance">
                      <div className="bg-white border border-emerald-300 p-2">
                        <div className="text-[9px] font-mono uppercase text-neutral-500">Cobrado</div>
                        <div className="font-mono text-sm text-emerald-700 font-bold">{currency(paid)}</div>
                      </div>
                      <div className={`bg-white border p-2 ${balance > 0 ? "border-[#FF3B30]" : "border-emerald-300"}`}>
                        <div className="text-[9px] font-mono uppercase text-neutral-500">Saldo pendiente</div>
                        <div className={`font-mono text-sm font-bold ${balance > 0 ? "text-[#FF3B30]" : "text-emerald-700"}`}>
                          {currency(balance)}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-3 flex items-center justify-between text-xs pt-2 border-t border-emerald-200">
                  <span className="font-mono uppercase text-neutral-500">Estado global de pago</span>
                  <span
                    className={`px-2 py-0.5 border font-medium ${
                      selected.payment_status === "paid"
                        ? "border-emerald-600 text-emerald-700 bg-white"
                        : selected.payment_status === "partial"
                          ? "border-amber-500 text-amber-700 bg-white"
                          : "border-neutral-300 text-neutral-600 bg-white"
                    }`}
                    data-testid="admin-drawer-paystatus"
                  >
                    {selected.payment_status === "paid" ? "Pagado" : selected.payment_status === "partial" ? "Parcial" : "Pendiente"}
                  </span>
                </div>
              </div>

              {/* Totals + manual override */}
              <div className="border-2 border-neutral-300 bg-white p-4" data-testid="admin-totals-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-sm">Totales</div>
                  {selected.manual_total_override != null && (
                    <span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-[#FF3B30] text-[#FF3B30]" data-testid="admin-total-override-badge">
                      Total manual
                    </span>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-neutral-600">
                    <span>Subtotal</span>
                    <span className="font-mono">{currency(selected.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-600">
                    <span>{selected.delivery_method === "sucursal" ? "Recolección" : "Envío"}</span>
                    <span className="font-mono">{(selected.shipping_fee || 0) === 0 ? "Gratis" : currency(selected.shipping_fee)}</span>
                  </div>
                  {/* IVA row + toggle */}
                  <div className="flex items-center justify-between py-1">
                    <label className="flex items-center gap-2 text-neutral-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selected.iva_enabled}
                        onChange={(e) => toggleIva(e.target.checked)}
                        className="w-4 h-4 accent-black"
                        data-testid="admin-total-iva-toggle"
                      />
                      <span>IVA 16%</span>
                      {selected.iva_enabled && (
                        <span className="text-[10px] font-mono uppercase text-emerald-700 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5">
                          Facturable
                        </span>
                      )}
                    </label>
                    <span className={`font-mono ${selected.iva_enabled ? "text-neutral-700" : "text-neutral-400"}`} data-testid="admin-total-iva">
                      {selected.iva_enabled ? currency(selected.iva_amount || 0) : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between font-display text-xl mt-1 pt-1 border-t border-neutral-200">
                    <span>Total</span>
                    <span className={selected.manual_total_override != null ? "text-[#FF3B30]" : ""} data-testid="admin-total-value">
                      {currency(selected.total || 0)}
                    </span>
                  </div>
                </div>

                {/* Manual total editor */}
                <div className="mt-3 pt-3 border-t border-neutral-200">
                  <Label className="text-xs font-mono uppercase text-neutral-500">Ajustar total manualmente (MXN)</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="rounded-none h-9 flex-1 font-mono"
                      value={manualTotalInput}
                      onChange={(e) => setManualTotalInput(e.target.value)}
                      data-testid="admin-total-manual-input"
                    />
                    <Button
                      onClick={saveManualTotal}
                      disabled={savingTotal || parseFloat(manualTotalInput) === Number(selected.total)}
                      className="rounded-none h-9 bg-black hover:bg-[#FF3B30] text-white text-xs px-4"
                      data-testid="admin-total-manual-save"
                    >
                      {savingTotal ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                      Guardar
                    </Button>
                  </div>
                  {selected.manual_total_override != null && (
                    <Button
                      variant="outline"
                      onClick={clearManualTotal}
                      disabled={savingTotal}
                      className="rounded-none h-8 w-full mt-2 border-neutral-300 text-neutral-600 hover:bg-neutral-50 text-xs"
                      data-testid="admin-total-manual-clear"
                    >
                      Eliminar override y recalcular
                    </Button>
                  )}
                  <p className="text-[10px] text-neutral-500 mt-2 leading-snug">
                    Al guardar un total manual, se conservará aunque edites los artículos. Elimina el override para volver al cálculo automático (subtotal + envío + IVA).
                  </p>
                </div>
              </div>

              <CustomerEditor order={selected} onSave={(payload) => saveCustomer(selected.order_id, payload)} />

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono uppercase text-neutral-500">
                    Artículos ({editMode
                      ? (draftItems.length + draftCustom.length)
                      : ((selected.items?.length || 0) + (selected.custom_items?.length || 0))})
                  </div>
                  {!editMode ? (
                    <Button size="sm" variant="outline" className="rounded-none h-8" onClick={startEditItems} data-testid="admin-edit-items-btn">
                      <Pencil className="w-3 h-3 mr-1" /> Editar artículos
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={cancelEditItems} disabled={savingItems} data-testid="admin-cancel-edit-items">
                        <Ban className="w-3 h-3 mr-1" /> Cancelar
                      </Button>
                      <Button size="sm" className="rounded-none h-8 bg-black hover:bg-[#FF3B30] text-white" onClick={saveItems} disabled={savingItems} data-testid="admin-save-items">
                        {savingItems ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                        Guardar cambios
                      </Button>
                    </div>
                  )}
                </div>

                {!editMode ? (
                  <div className="space-y-3">
                    {selected.items.map((it, i) => (
                      <div key={i} className="border border-neutral-200 p-3 flex gap-3" data-testid={`admin-order-item-${i}`}>
                        <div className="w-16 h-16 bg-neutral-100 flex-shrink-0 overflow-hidden">
                          <img src={it.product_image} alt={it.product_name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{it.product_name}</div>
                          <div className="text-xs text-neutral-500 font-mono">
                            {it.color_label && `${it.color_label} · `}
                            {it.size_label && `${it.size_label} · `}
                            x{it.quantity}
                          </div>
                          {it.design_url && (
                            <a
                              href={`${API}/files/${it.design_url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-600 underline mt-1 inline-block"
                              data-testid={`admin-design-link-${i}`}
                            >
                              Ver diseño {it.design_filename && `(${it.design_filename})`}
                            </a>
                          )}
                          {it.notes && <div className="text-xs italic text-neutral-500 mt-1">&quot;{it.notes}&quot;</div>}
                        </div>
                        <div className="text-sm font-medium">{currency(it.line_total)}</div>
                      </div>
                    ))}
                    {(selected.custom_items || []).map((ci, i) => (
                      <div key={`c-${i}`} className="border-2 border-amber-400 bg-amber-50/40 p-3 flex gap-3" data-testid={`admin-custom-item-${i}`}>
                        <div className="w-16 h-16 bg-amber-100 flex-shrink-0 flex items-center justify-center text-[10px] font-mono uppercase text-amber-800">
                          Cotización
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{ci.name}</div>
                          {ci.description && <div className="text-xs text-neutral-600 mt-0.5">{ci.description}</div>}
                          <div className="text-xs text-neutral-500 font-mono mt-1">
                            {currency(ci.unit_price)} c/u · x{ci.quantity}
                          </div>
                        </div>
                        <div className="text-sm font-medium">{currency(ci.line_total)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {draftItems.length === 0 && (
                      <div className="border border-dashed border-red-300 bg-red-50 p-3 text-xs text-red-700 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Sin artículos. Agrega al menos uno o elimina el pedido desde el bloque inferior.</span>
                      </div>
                    )}
                    {draftItems.map((d, i) => (
                      <div key={i} className="border border-neutral-200 p-3 flex gap-3 items-start" data-testid={`admin-draft-item-${i}`}>
                        <div className="w-14 h-14 bg-neutral-100 flex-shrink-0 overflow-hidden">
                          <img src={d._product_image} alt={d._product_name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{d._product_name}</div>
                          <div className="text-xs text-neutral-500 font-mono">
                            {d.color_label && `${d.color_label} · `}
                            {d.size_label && `${d.size_label}`}
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => changeDraftQty(i, -1)}
                                className="w-7 h-7 border border-neutral-200 hover:bg-neutral-100 flex items-center justify-center"
                                data-testid={`admin-draft-qty-minus-${i}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={d.quantity}
                                onChange={(e) => setDraftQty(i, e.target.value)}
                                className="w-14 h-7 border border-neutral-200 text-center text-xs outline-none"
                                data-testid={`admin-draft-qty-${i}`}
                              />
                              <button
                                onClick={() => changeDraftQty(i, 1)}
                                className="w-7 h-7 border border-neutral-200 hover:bg-neutral-100 flex items-center justify-center"
                                data-testid={`admin-draft-qty-plus-${i}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-mono uppercase text-neutral-500">P.U.</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="auto"
                                value={d.override_unit_price ?? ""}
                                onChange={(e) => setDraftOverride(i, e.target.value)}
                                className={`w-24 h-7 border text-xs px-1 outline-none font-mono ${
                                  d.override_unit_price != null ? "border-[#FF3B30] text-[#FF3B30] bg-red-50/40" : "border-neutral-200"
                                }`}
                                data-testid={`admin-draft-price-${i}`}
                                title="Precio unitario manual (ignora reglas de mayoreo y variantes)"
                              />
                              {d.override_unit_price != null && (
                                <button
                                  type="button"
                                  onClick={() => setDraftOverride(i, "")}
                                  className="text-[10px] font-mono uppercase text-neutral-500 hover:text-red-600 underline"
                                  data-testid={`admin-draft-price-clear-${i}`}
                                >
                                  auto
                                </button>
                              )}
                            </div>
                            <div className="text-[10px] font-mono text-neutral-500 ml-auto">
                              = {currency(d._line_total || 0)}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeDraftItem(i)}
                          className="p-2 hover:bg-red-50 text-red-600"
                          data-testid={`admin-draft-remove-${i}`}
                          title="Eliminar artículo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    {/* Add product picker */}
                    <div className="border border-dashed border-neutral-300 p-3 space-y-2 bg-neutral-50" data-testid="admin-add-item-picker">
                      <div className="text-xs font-mono uppercase text-neutral-500">Agregar producto al pedido</div>
                      <select
                        className="w-full border border-neutral-200 rounded-none px-2 h-9 text-sm bg-white"
                        value={pickerProductId}
                        onChange={(e) => setPickerProductId(e.target.value)}
                        data-testid="admin-picker-product"
                      >
                        <option value="">Selecciona un producto...</option>
                        {allProducts.filter((p) => p.active !== false).map((p) => (
                          <option key={p.product_id} value={p.product_id}>
                            {p.name} — {currency(p.base_price)}
                            {p.min_qty > 1 ? ` (mín. ${p.min_qty})` : ""}
                          </option>
                        ))}
                      </select>
                      {pickerProduct && (
                        <div className="grid grid-cols-3 gap-2">
                          {pickerProduct.colors?.length > 0 && (
                            <select
                              className="border border-neutral-200 rounded-none px-2 h-9 text-xs bg-white"
                              value={pickerColor}
                              onChange={(e) => setPickerColor(e.target.value)}
                              data-testid="admin-picker-color"
                            >
                              {pickerProduct.colors.map((c) => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          )}
                          {pickerProduct.sizes?.length > 0 && (
                            <select
                              className="border border-neutral-200 rounded-none px-2 h-9 text-xs bg-white"
                              value={pickerSize}
                              onChange={(e) => setPickerSize(e.target.value)}
                              data-testid="admin-picker-size"
                            >
                              {pickerProduct.sizes.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          )}
                          <div>
                            <Input
                              type="number"
                              min={1}
                              className="rounded-none h-9 text-xs"
                              value={pickerQty}
                              onChange={(e) => setPickerQty(Math.max(1, parseInt(e.target.value || 1, 10)))}
                              placeholder="Cant."
                              data-testid="admin-picker-qty"
                            />
                            {pickerProduct.min_qty > 1 && (
                              <div className="text-[10px] text-neutral-500 mt-1 italic">
                                Tienda: mín. {pickerProduct.min_qty} · admin sin restricción
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <Button
                        size="sm"
                        className="rounded-none h-8 w-full bg-black hover:bg-[#FF3B30] text-white"
                        onClick={addDraftItemFromPicker}
                        disabled={!pickerProduct}
                        data-testid="admin-picker-add"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Agregar al pedido
                      </Button>
                    </div>

                    {/* Custom (ad-hoc) items — quote-only, not saved in catalog */}
                    <div className="border-2 border-amber-500/70 bg-amber-50/40 p-3 space-y-2" data-testid="admin-custom-items-block">
                      <div className="flex items-center gap-2 mb-1">
                        <Plus className="w-4 h-4 text-amber-700" />
                        <div className="font-medium text-sm">Producto personalizado (cotización)</div>
                      </div>
                      <div className="text-[11px] text-neutral-600 italic mb-2">
                        Agrega ítems manuales que <em>no están dados de alta en el sistema</em>. Solo se guardan en este pedido para cotización.
                      </div>
                      {draftCustom.length > 0 && (
                        <ul className="space-y-2" data-testid="admin-custom-items-list">
                          {draftCustom.map((c, i) => (
                            <li key={i} className="border border-amber-300 bg-white p-2 grid grid-cols-12 gap-1.5 items-start" data-testid={`admin-custom-item-row-${i}`}>
                              <input
                                type="text"
                                value={c.name}
                                onChange={(e) => setDraftCustomField(i, "name", e.target.value)}
                                placeholder="Nombre / descripción"
                                maxLength={200}
                                className="col-span-12 h-8 text-xs px-2 border border-neutral-300"
                                data-testid={`admin-custom-item-name-${i}`}
                              />
                              <input
                                type="text"
                                value={c.description || ""}
                                onChange={(e) => setDraftCustomField(i, "description", e.target.value)}
                                placeholder="Notas / detalles (opcional)"
                                maxLength={500}
                                className="col-span-12 h-8 text-xs px-2 border border-neutral-200"
                                data-testid={`admin-custom-item-desc-${i}`}
                              />
                              <input
                                type="number"
                                min={1}
                                value={c.quantity}
                                onChange={(e) => setDraftCustomField(i, "quantity", e.target.value)}
                                className="col-span-3 h-8 text-xs px-2 border border-neutral-300"
                                data-testid={`admin-custom-item-qty-${i}`}
                                title="Cantidad"
                              />
                              <input
                                type="number"
                                step="0.01"
                                min={0}
                                value={c.unit_price}
                                onChange={(e) => setDraftCustomField(i, "unit_price", e.target.value)}
                                className="col-span-4 h-8 text-xs px-2 border border-neutral-300"
                                data-testid={`admin-custom-item-price-${i}`}
                                title="Costo unitario"
                              />
                              <div className="col-span-4 flex items-center h-8 px-2 font-medium text-xs text-neutral-700 font-mono">
                                {currency(Number(c.unit_price || 0) * Number(c.quantity || 0))}
                              </div>
                              <button
                                onClick={() => removeDraftCustom(i)}
                                className="col-span-1 h-8 flex items-center justify-center text-neutral-500 hover:text-red-600"
                                title="Quitar"
                                data-testid={`admin-custom-item-remove-${i}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="grid grid-cols-12 gap-1.5 pt-1">
                        <input
                          type="text"
                          value={customName}
                          onChange={(e) => setCustomName(e.target.value)}
                          placeholder="Nombre / descripción"
                          maxLength={200}
                          className="col-span-12 h-9 text-xs px-2 border border-neutral-300"
                          data-testid="admin-custom-item-input-name"
                        />
                        <input
                          type="text"
                          value={customDescription}
                          onChange={(e) => setCustomDescription(e.target.value)}
                          placeholder="Notas / detalles (opcional)"
                          maxLength={500}
                          className="col-span-12 h-9 text-xs px-2 border border-neutral-300"
                          data-testid="admin-custom-item-input-desc"
                        />
                        <input
                          type="number"
                          min={1}
                          value={customQty}
                          onChange={(e) => setCustomQty(Math.max(1, parseInt(e.target.value || 1, 10)))}
                          className="col-span-4 h-9 text-xs px-2 border border-neutral-300"
                          placeholder="Cant."
                          data-testid="admin-custom-item-input-qty"
                        />
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          value={customPrice}
                          onChange={(e) => setCustomPrice(e.target.value)}
                          className="col-span-4 h-9 text-xs px-2 border border-neutral-300"
                          placeholder="Costo unitario"
                          data-testid="admin-custom-item-input-price"
                        />
                        <Button
                          size="sm"
                          onClick={addDraftCustom}
                          className="col-span-4 h-9 rounded-none bg-amber-700 hover:bg-amber-800 text-white text-xs"
                          data-testid="admin-custom-item-add"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Agregar
                        </Button>
                      </div>
                    </div>

                    <div className="border border-neutral-200 bg-neutral-50 p-3 text-xs space-y-1" data-testid="admin-edit-preview-totals">
                      <div className="flex justify-between"><span>Subtotal estimado</span><span>{currency(previewTotals.subtotal)}</span></div>
                      <div className="flex justify-between"><span>Envío</span><span>{previewTotals.shipping === 0 ? "Gratis" : currency(previewTotals.shipping)}</span></div>
                      <div className="flex justify-between font-medium border-t border-neutral-200 pt-1 mt-1"><span>Total estimado</span><span>{currency(previewTotals.total)}</span></div>
                      <div className="text-[10px] text-neutral-500 italic">El servidor recalcula con reglas de mayoreo y variantes al guardar.</div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs font-mono uppercase text-neutral-500 mb-2">Historial</div>
                <ul className="space-y-2 text-xs">
                  {[...(selected.history || [])].reverse().map((h, i) => (
                    <li key={i} className="border-l-2 border-neutral-200 pl-3">
                      <div className="font-mono text-neutral-500">{new Date(h.at).toLocaleString("es-MX")}</div>
                      <div>{h.message}</div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Danger zone: delete order */}
              <div className="border-2 border-red-500/70 bg-red-50/40 p-4" data-testid="admin-danger-zone">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-red-900">Eliminar pedido</div>
                    <div className="text-xs text-neutral-700 mt-1">
                      Borra el pedido de forma permanente. Solo úsalo para pedidos de prueba o duplicados. Si el cliente canceló, prefiere marcarlo como <em>Cancelado</em>.
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="rounded-none w-full mt-3 border-red-500 text-red-700 hover:bg-red-100"
                  onClick={deleteOrder}
                  disabled={deleting}
                  data-testid="admin-delete-order"
                >
                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Eliminar pedido definitivamente
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Editable Customer + Shipping block for the admin drawer.
// Save button appears only when values differ from the current order.
function CustomerEditor({ order, onSave }) {
  const [editing, setEditing] = useState(false);
  const initial = {
    name: order.shipping?.name || order.user_name || "",
    email: order.user_email || order.guest_email || "",
    phone: order.shipping?.phone || "",
    address: order.shipping?.address || "",
    city: order.shipping?.city || "",
    state: order.shipping?.state || "",
    zip: order.shipping?.zip || "",
  };
  const [form, setForm] = useState(initial);
  useEffect(() => {
    setForm({
      name: order.shipping?.name || order.user_name || "",
      email: order.user_email || order.guest_email || "",
      phone: order.shipping?.phone || "",
      address: order.shipping?.address || "",
      city: order.shipping?.city || "",
      state: order.shipping?.state || "",
      zip: order.shipping?.zip || "",
    });
  }, [order.order_id, order.user_name, order.user_email, order.guest_email, order.shipping]);

  const isSucursal = order.delivery_method === "sucursal";
  const dirty = ["name", "email", "phone", "address", "city", "state", "zip"].some(
    (k) => (form[k] || "").trim() !== (initial[k] || "").trim()
  );

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const payload = {};
    for (const k of ["name", "email", "phone", "address", "city", "state", "zip"]) {
      if ((form[k] || "").trim() !== (initial[k] || "").trim()) {
        payload[k] = (form[k] || "").trim();
      }
    }
    if (Object.keys(payload).length === 0) return;
    await onSave(payload);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase text-neutral-500">
            Cliente · {isSucursal ? "Recolección en sucursal" : "Envío a domicilio"}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-none h-7 text-xs"
            onClick={() => setEditing(true)}
            data-testid="admin-drawer-edit-customer"
          >
            <Pencil className="w-3 h-3 mr-1" /> Editar
          </Button>
        </div>
        <div className="text-sm">{form.name || <span className="italic text-neutral-400">Sin nombre</span>}</div>
        <div className="text-xs text-neutral-500">{form.email || <span className="italic text-neutral-400">Sin correo</span>}</div>
        <div className="text-xs text-neutral-600">{form.phone || <span className="italic text-neutral-400">Sin teléfono</span>}</div>
        {isSucursal ? (
          <div className="text-xs text-neutral-500 italic mt-1">El cliente recogerá el pedido en sucursal.</div>
        ) : (
          <>
            <div className="text-xs text-neutral-600 mt-1">{form.address}</div>
            <div className="text-xs text-neutral-600">{form.city}{form.city && form.state ? ", " : ""}{form.state} {form.zip}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 border-2 border-black p-3" data-testid="admin-drawer-customer-form">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-mono uppercase text-neutral-700">Editar datos del cliente</div>
        <button
          onClick={() => { setForm(initial); setEditing(false); }}
          className="text-xs text-neutral-500 hover:text-black underline"
          data-testid="admin-drawer-customer-cancel"
        >
          Cancelar
        </button>
      </div>
      <div className="grid grid-cols-12 gap-2">
        <label className="col-span-12 text-[10px] font-mono uppercase text-neutral-500">
          Nombre
          <Input value={form.name} onChange={(e) => setField("name", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-name" />
        </label>
        <label className="col-span-7 text-[10px] font-mono uppercase text-neutral-500">
          Email
          <Input value={form.email} onChange={(e) => setField("email", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-email" />
        </label>
        <label className="col-span-5 text-[10px] font-mono uppercase text-neutral-500">
          Teléfono
          <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-phone" />
        </label>
        {!isSucursal && (
          <>
            <label className="col-span-12 text-[10px] font-mono uppercase text-neutral-500">
              Dirección
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-address" />
            </label>
            <label className="col-span-5 text-[10px] font-mono uppercase text-neutral-500">
              Ciudad
              <Input value={form.city} onChange={(e) => setField("city", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-city" />
            </label>
            <label className="col-span-4 text-[10px] font-mono uppercase text-neutral-500">
              Estado
              <Input value={form.state} onChange={(e) => setField("state", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-state" />
            </label>
            <label className="col-span-3 text-[10px] font-mono uppercase text-neutral-500">
              CP
              <Input value={form.zip} onChange={(e) => setField("zip", e.target.value)} className="rounded-none h-8 text-sm mt-0.5" data-testid="admin-cust-zip" />
            </label>
          </>
        )}
      </div>
      <div className="flex justify-end pt-1">
        <Button
          size="sm"
          className="rounded-none h-8 bg-black hover:bg-neutral-800 text-white text-xs"
          onClick={submit}
          disabled={!dirty}
          data-testid="admin-drawer-customer-save"
        >
          <Save className="w-3 h-3 mr-1" /> Guardar cliente
        </Button>
      </div>
    </div>
  );
}


// Compact QR + folio panel for the admin drawer. The QR now points to the
// CUSTOMER-facing public panel (/mi-pedido/:order_number) so the admin can
// preview/copy exactly what the client will see. Also shows customer
// authorization status prominently.
function OrderQRPanel({ order }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [composing, setComposing] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const hasPublicPanel = !!order.order_number;
  const publicLink = hasPublicPanel
    ? `${window.location.origin}/mi-pedido/${order.order_number}`
    : null;
  const adminLink = `${window.location.origin}/admin?order_id=${encodeURIComponent(order.order_id)}`;

  // Editable share message. Persisted per-order in localStorage so the admin's
  // custom wording survives drawer close/reopen without needing a backend round-trip.
  const defaultMessage = useMemo(() => (
    `📎 Hola, aquí está el panel para tu pedido #${order.order_number || order.order_id} de Printec.\n\nEscanea el QR o toca el enlace para revisar y AUTORIZAR tu pedido:\n${publicLink || ""}`
  ), [order.order_number, order.order_id, publicLink]);
  const storageKey = `share-msg-${order.order_id}`;
  const [shareMessage, setShareMessage] = useState(() => {
    try { return localStorage.getItem(storageKey) || defaultMessage; }
    catch (_e) { return defaultMessage; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, shareMessage); } catch (_e) { /* noop */ }
  }, [storageKey, shareMessage]);
  const resetMessage = () => setShareMessage(defaultMessage);

  useEffect(() => {
    const target = publicLink || adminLink;
    QRCode.toDataURL(target, { margin: 1, width: 180, color: { dark: "#0A0A0A", light: "#FFFFFF" } })
      .then(setQrUrl)
      .catch(() => setQrUrl(null));
  }, [publicLink, adminLink]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copiado`),
      () => toast.error("No se pudo copiar")
    );
  };

  // Build a composite PNG with QR + folio + link (so the admin can share a
  // SINGLE image via WhatsApp — the client scans or taps the URL printed below).
  const buildComposite = async () => {
    if (!publicLink) return null;
    const bigQr = await QRCode.toDataURL(publicLink, { margin: 1, width: 700, color: { dark: "#0A0A0A", light: "#FFFFFF" } });
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = bigQr;
    });
    const W = 900;
    const H = 1180;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, W, H);
    // Header
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "bold 36px Helvetica, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("PANEL DE TU PEDIDO", W / 2, 70);
    ctx.font = "22px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#555";
    ctx.fillText("Escanea el QR o abre el enlace para autorizar", W / 2, 108);
    // Folio
    ctx.font = "bold 72px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#0A0A0A";
    ctx.fillText(`#${order.order_number}`, W / 2, 190);
    // QR
    ctx.drawImage(img, (W - 700) / 2, 230, 700, 700);
    // Link band
    ctx.fillStyle = "#F3F4F6";
    ctx.fillRect(60, 960, W - 120, 100);
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2;
    ctx.strokeRect(60, 960, W - 120, 100);
    ctx.font = "18px 'Courier New', monospace";
    ctx.fillStyle = "#0A0A0A";
    ctx.textAlign = "center";
    ctx.fillText("ENLACE DIRECTO", W / 2, 988);
    ctx.font = "bold 22px 'Courier New', monospace";
    ctx.fillText(publicLink, W / 2, 1030);
    // Footer
    ctx.font = "18px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#777";
    ctx.fillText("Dudas · WhatsApp al negocio", W / 2, 1110);
    return new Promise((resolve) => c.toBlob(resolve, "image/png"));
  };

  const copyAsImage = async () => {
    if (!publicLink || composing) return;
    setComposing(true);
    try {
      const blob = await buildComposite();
      if (!blob) throw new Error("No se pudo generar la imagen");
      // Shareable text with embedded link — uses the admin-customized message.
      const shareText = shareMessage || defaultMessage;
      // Modern clipboard: write BOTH formats so pasting in a text field gets
      // the clickable URL and pasting in a media area gets the image.
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "image/png": blob,
              "text/plain": new Blob([shareText], { type: "text/plain" }),
            }),
          ]);
          toast.success("QR + enlace copiados · pégalos en WhatsApp (el link será clickeable)");
          return;
        } catch (_e) {
          // Some browsers only allow a single mime type; fall back to text (clickable)
          try {
            await navigator.clipboard.writeText(shareText);
            toast.success("Enlace copiado como texto clickeable · descargando QR");
          } catch (_e2) { /* keep going to download */ }
        }
      }
      // Final fallback: download the composite image
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `panel-cliente-${order.order_number}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo copiar · descarga la imagen desde el botón de abajo");
    } finally {
      setComposing(false);
    }
  };

  const shareOnWhatsApp = () => {
    if (!publicLink) return;
    const phone = (order?.shipping?.phone || order?.contact?.phone || "").replace(/\D/g, "");
    const text = shareMessage || defaultMessage;
    const url = phone
      ? `https://wa.me/${phone.length === 10 ? "52" + phone : phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadImage = async () => {
    if (!publicLink || composing) return;
    setComposing(true);
    try {
      const blob = await buildComposite();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `panel-cliente-${order.order_number}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setComposing(false);
    }
  };

  const approved = !!order.customer_approved;
  const approvedAt = order.customer_approved_at
    ? new Date(order.customer_approved_at).toLocaleString("es-MX", { timeZone: "America/Monterrey", hour12: false })
    : null;

  return (
    <div className="space-y-2" data-testid="admin-drawer-qr-panel">
      {/* Authorization status banner */}
      {hasPublicPanel && (
        approved ? (
          <div
            className="border-2 border-emerald-600 bg-emerald-50 p-3 flex items-start gap-2"
            data-testid="admin-drawer-approved-banner"
          >
            <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-emerald-900">Cliente autorizó el pedido ✓</div>
              <div className="text-[11px] font-mono text-emerald-800 mt-0.5">
                {approvedAt}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="border-2 border-amber-500 bg-amber-50 p-3 flex items-start gap-2"
            data-testid="admin-drawer-unapproved-banner"
          >
            <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-amber-900">Sin autorización del cliente</div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                Comparte el enlace de abajo para que el cliente revise y autorice.
              </div>
            </div>
          </div>
        )
      )}

      {/* QR + link block */}
      <div className="border-2 border-black p-3 flex items-start gap-3">
        {qrUrl ? (
          <img src={qrUrl} alt="QR" className="w-24 h-24 shrink-0" data-testid="admin-drawer-qr-img" />
        ) : (
          <div className="w-24 h-24 bg-neutral-100 shrink-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase text-neutral-500">Folio</div>
          <div className="font-display text-3xl leading-none">
            {order.order_number ? `#${order.order_number}` : order.order_id}
          </div>
          {hasPublicPanel ? (
            <>
              <div className="text-[10px] text-neutral-500 mt-1.5">
                QR y enlace del <span className="font-bold">panel público del cliente</span>. Aquí el cliente ve su pedido y lo autoriza.
              </div>
              <div className="mt-1.5 flex flex-wrap gap-2">
                <button
                  onClick={copyAsImage}
                  disabled={composing}
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-black text-white hover:bg-[#FF3B30] px-2 py-1 disabled:opacity-50"
                  data-testid="admin-drawer-qr-copy-image"
                  title="Copia QR como imagen + enlace clickeable (texto) para pegar en WhatsApp"
                >
                  {composing ? <Loader2 className="w-3 h-3 animate-spin" /> : <>📎 Copiar QR + enlace</>}
                </button>
                <button
                  onClick={shareOnWhatsApp}
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-emerald-600 text-white hover:bg-black px-2 py-1"
                  data-testid="admin-drawer-qr-wa"
                  title="Abre WhatsApp con el enlace clickeable listo para enviar"
                >
                  💬 Enviar por WhatsApp
                </button>
                <button
                  onClick={downloadImage}
                  disabled={composing}
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-neutral-700 hover:text-black underline disabled:opacity-50"
                  data-testid="admin-drawer-qr-download"
                  title="Descarga una imagen PNG con QR + enlace"
                >
                  <FileDown className="w-3 h-3" /> Descargar PNG
                </button>
                <button
                  onClick={() => copy(publicLink, "Enlace del cliente")}
                  className="text-[10px] font-mono uppercase text-neutral-700 hover:text-black underline"
                  data-testid="admin-drawer-qr-copy"
                >
                  Solo texto
                </button>
                <a
                  href={publicLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-neutral-700 hover:text-black underline"
                  data-testid="admin-drawer-open-public"
                >
                  <ExternalLink className="w-3 h-3" /> Abrir vista cliente
                </a>
              </div>

              {/* Editable share message */}
              <div className="mt-2 pt-2 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={() => setMessageOpen((v) => !v)}
                  className="text-[10px] font-mono uppercase text-neutral-600 hover:text-black flex items-center gap-1"
                  data-testid="admin-drawer-message-toggle"
                >
                  <Pencil className="w-3 h-3" />
                  {messageOpen ? "Ocultar mensaje" : "Editar mensaje para el cliente"}
                </button>
                {messageOpen && (
                  <div className="mt-2 space-y-1" data-testid="admin-drawer-message-editor">
                    <textarea
                      value={shareMessage}
                      onChange={(e) => setShareMessage(e.target.value)}
                      rows={6}
                      className="w-full border border-neutral-300 p-2 text-xs font-mono outline-none focus:border-black resize-y"
                      data-testid="admin-drawer-message-input"
                      placeholder="Mensaje que se copiará/enviará al cliente…"
                    />
                    <div className="flex items-center justify-between">
                      <div className="text-[9px] font-mono text-neutral-500">
                        Se usa en &quot;Copiar QR + enlace&quot; y &quot;Enviar por WhatsApp&quot;. Incluye el enlace para que sea clickeable.
                      </div>
                      <button
                        type="button"
                        onClick={resetMessage}
                        className="text-[10px] font-mono uppercase text-neutral-600 hover:text-[#FF3B30] underline"
                        data-testid="admin-drawer-message-reset"
                      >
                        Restaurar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] text-neutral-500 mt-1.5">
                Pedido antiguo (sin folio secuencial): usa el enlace admin.
              </div>
              <button
                onClick={() => copy(adminLink, "Enlace admin")}
                className="mt-1.5 text-[10px] font-mono uppercase text-neutral-600 hover:text-black underline"
                data-testid="admin-drawer-qr-copy"
              >
                Copiar enlace
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Compact list of an order's items shown directly in the orders table row.
// Shows up to 3 lines with product name + variant (color/size/printing) + qty.
function ItemsSummary({ order }) {
  const items = order.items || [];
  const custom = order.custom_items || [];
  const total = items.length + custom.length;
  const MAX_ROWS = 3;
  const rows = [];
  for (const it of items) {
    const parts = [];
    if (it.color_label) parts.push(it.color_label);
    if (it.size_label) parts.push(it.size_label);
    if (it.printing_method_label) parts.push(it.printing_method_label);
    rows.push({
      key: `p-${rows.length}`,
      name: it.product_name,
      variants: parts.join(" · "),
      qty: it.quantity,
      isCustom: false,
    });
  }
  for (const ci of custom) {
    rows.push({
      key: `c-${rows.length}`,
      name: ci.name,
      variants: ci.description || "",
      qty: ci.quantity,
      isCustom: true,
    });
  }
  const visible = rows.slice(0, MAX_ROWS);
  const remaining = rows.length - visible.length;

  if (total === 0) {
    return <span className="text-xs text-neutral-400 italic">Sin ítems</span>;
  }
  return (
    <div className="text-[11px] leading-snug space-y-1 max-w-[260px]" data-testid={`admin-row-items-${order.order_id}`}>
      {visible.map((r) => (
        <div key={r.key} className="flex items-start gap-1.5">
          <span className={`inline-block px-1 font-mono text-[10px] rounded-none ${
            r.isCustom ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-700"
          }`}>
            x{r.qty}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-neutral-800 truncate" title={r.name}>{r.name}</div>
            {r.variants && (
              <div className="text-neutral-500 truncate font-mono text-[10px]" title={r.variants}>
                {r.variants}
              </div>
            )}
          </div>
        </div>
      ))}
      {remaining > 0 && (
        <div className="text-[10px] text-neutral-500 italic">+{remaining} ítem{remaining === 1 ? "" : "s"} más</div>
      )}
    </div>
  );
}


// Inline editable "Proyecto" cell/field for the orders table.
// Commits on blur or Ctrl/Cmd+Enter; Esc reverts. Multi-line so 3 rows are visible at a glance.
function ProjectInline({ order, onSave }) {
  const initial = order.project || "";
  const [value, setValue] = useState(initial);
  useEffect(() => {
    setValue(order.project || "");
  }, [order.order_id, order.project]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed === (order.project || "")) return;
    onSave(trimmed);
  };

  return (
    <textarea
      value={value}
      maxLength={500}
      rows={3}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          setValue(order.project || "");
          e.currentTarget.blur();
        }
      }}
      placeholder="—"
      className="w-64 px-2 py-1 text-xs border border-transparent hover:border-neutral-300 focus:border-black focus:outline-none bg-transparent focus:bg-white font-mono resize-y leading-snug align-top whitespace-pre-wrap"
      data-testid={`admin-row-project-${order.order_id}`}
      title="Etiqueta / notas internas del proyecto. Ctrl+Enter guarda, Esc revierte."
    />
  );
}


// Drawer variant of the project editor with a Save button.
function ProjectDrawerField({ order, onSave }) {
  const [value, setValue] = useState(order.project || "");
  useEffect(() => {
    setValue(order.project || "");
  }, [order.order_id, order.project]);

  const dirty = value.trim() !== (order.project || "");

  return (
    <div className="flex items-start gap-2">
      <textarea
        value={value}
        maxLength={500}
        rows={4}
        onChange={(e) => setValue(e.target.value)}
        placeholder="ej. Uniformes Escuela ABC 2026 — grabado dorado, entrega jueves 10, contacto Ana 555-1234"
        className="flex-1 min-h-[96px] px-3 py-2 text-sm border border-neutral-300 rounded-none focus:border-black focus:outline-none resize-y leading-snug whitespace-pre-wrap"
        data-testid="admin-drawer-project-input"
      />
      <Button
        size="sm"
        className="rounded-none h-9"
        disabled={!dirty}
        onClick={() => onSave(value.trim())}
        data-testid="admin-drawer-project-save"
      >
        <Save className="w-3.5 h-3.5 mr-1.5" /> Guardar
      </Button>
    </div>
  );
}


// Notifications history + manual log.
// Formats the date in America/Monterrey (business timezone).
function NotificationsLog({ order, onDelete, onLogManual }) {
  const notifications = order?.notifications || [];
  const [note, setNote] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [dateStr, setDateStr] = useState(""); // YYYY-MM-DD; if empty, uses now

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-MX", {
        timeZone: "America/Monterrey",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const submitManual = () => {
    let atISO = null;
    if (dateStr) {
      // Interpret the picked date at local 12:00 Monterrey to avoid TZ-off-by-one.
      // JS Date parses "YYYY-MM-DDT12:00:00-06:00" reliably.
      atISO = new Date(`${dateStr}T12:00:00-06:00`).toISOString();
    }
    onLogManual({ type: "manual", channel, note: note.trim() || null, at: atISO });
    setNote("");
    setDateStr("");
  };

  return (
    <div className="mt-3 pt-3 border-t border-emerald-300/60" data-testid="admin-wa-log-block">
      <div className="text-[11px] font-mono uppercase text-neutral-600 mb-2">
        Historial de notificaciones ({notifications.length})
      </div>
      {notifications.length === 0 ? (
        <div className="text-[11px] text-neutral-500 italic">
          Aún no se ha registrado ninguna notificación.
        </div>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="admin-wa-log-list">
          {notifications.map((n, idx) => (
            <li
              key={idx}
              className="flex items-start justify-between gap-2 text-[11px] bg-white border border-emerald-200 px-2 py-1"
              data-testid={`admin-wa-log-item-${idx}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] text-neutral-500">{fmt(n.at)}</div>
                <div className="text-neutral-700">
                  <span className="uppercase font-semibold">{n.channel || "whatsapp"}</span>
                  {" · "}
                  <span>{n.type}</span>
                  {n.note ? <span className="text-neutral-500"> — {n.note}</span> : null}
                </div>
                {n.by ? <div className="text-[9px] text-neutral-400 font-mono">por {n.by}</div> : null}
              </div>
              <button
                onClick={() => onDelete(idx)}
                className="text-neutral-400 hover:text-red-600 shrink-0"
                title="Eliminar registro"
                data-testid={`admin-wa-log-delete-${idx}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Manual log form */}
      <div className="mt-2 grid grid-cols-12 gap-1.5" data-testid="admin-wa-log-form">
        <input
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          className="col-span-4 h-8 text-[11px] px-1.5 border border-neutral-300"
          title="Día en que se notificó (opcional; por defecto es hoy)"
          data-testid="admin-wa-log-date"
        />
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="col-span-3 h-8 text-[11px] px-1.5 border border-neutral-300 bg-white"
          data-testid="admin-wa-log-channel"
        >
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
          <option value="phone">Llamada</option>
          <option value="manual">Presencial</option>
          <option value="otro">Otro</option>
        </select>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota (opcional)"
          maxLength={140}
          className="col-span-5 h-8 text-[11px] px-1.5 border border-neutral-300"
          data-testid="admin-wa-log-note"
        />
        <Button
          onClick={submitManual}
          size="sm"
          className="col-span-12 h-8 rounded-none bg-emerald-700 hover:bg-emerald-800 text-white text-[11px]"
          data-testid="admin-wa-log-submit"
        >
          <CalendarDays className="w-3 h-3 mr-1" /> Registrar notificación
        </Button>
      </div>
    </div>
  );
}

// --- Admin block: upload design mockups so the customer can review & authorize them ---
function AdminDesignsBlock({ order, onUpdate }) {
  const designs = order.admin_designs || [];
  const [uploading, setUploading] = React.useState(false);
  const [label, setLabel] = React.useState("");
  const [note, setNote] = React.useState("");
  const inputRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Máximo 20 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (label.trim()) form.append("label", label.trim());
      if (note.trim()) form.append("note", note.trim());
      const { data } = await http.post(`/admin/orders/${order.order_id}/designs`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate(data);
      setLabel(""); setNote("");
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Diseño subido · el cliente lo verá en su panel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir");
    } finally {
      setUploading(false);
    }
  };

  const removeDesign = async (i) => {
    if (!window.confirm("¿Eliminar este diseño?")) return;
    try {
      const { data } = await http.delete(`/admin/orders/${order.order_id}/designs/${i}`);
      onUpdate(data);
      toast.success("Eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <div className="border-2 border-black bg-neutral-50 p-3" data-testid="admin-drawer-designs-block">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-mono uppercase font-bold">Diseños propuestos ({designs.length})</div>
          <div className="text-[10px] text-neutral-500">Súbelos aquí para que el cliente los revise y autorice desde su panel.</div>
        </div>
      </div>

      {designs.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {designs.map((d, i) => {
            const url = `${API}/files/${d.storage_path}`;
            const isImg = (d.content_type || "").startsWith("image/");
            return (
              <div key={i} className="border-2 border-black bg-white p-2 relative" data-testid={`admin-design-${i}`}>
                <button
                  onClick={() => removeDesign(i)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF3B30] text-white hover:bg-black flex items-center justify-center z-10"
                  data-testid={`admin-design-remove-${i}`}
                  title="Eliminar"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
                <a href={url} target="_blank" rel="noopener noreferrer" className="block w-full h-24 bg-neutral-100 flex items-center justify-center overflow-hidden">
                  {isImg ? (
                    <img src={url} alt={d.filename} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <FileDown className="w-8 h-8 text-neutral-400" />
                  )}
                </a>
                {d.label && <div className="text-[10px] font-bold mt-1 truncate" title={d.label}>{d.label}</div>}
                <div className="text-[9px] font-mono text-neutral-500 truncate" title={d.filename}>{d.filename}</div>
                {d.note && <div className="text-[9px] italic text-neutral-600 mt-0.5">"{d.note}"</div>}
              </div>
            );
          })}
        </div>
      )}

      <div className="border border-dashed border-neutral-400 bg-white p-2 space-y-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Nombre del diseño (ej. Mockup frontal v1)"
          className="w-full h-8 px-2 border border-neutral-300 text-xs outline-none focus:border-black"
          data-testid="admin-design-label-input"
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota para el cliente (opcional)"
          className="w-full h-8 px-2 border border-neutral-300 text-xs outline-none focus:border-black"
          data-testid="admin-design-note-input"
        />
        <label className={`flex items-center justify-center gap-2 h-10 border-2 border-dashed cursor-pointer transition-colors ${uploading ? "pointer-events-none opacity-60 border-neutral-300" : "border-black hover:bg-black hover:text-white"}`} data-testid="admin-design-dropzone">
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={uploading}
            data-testid="admin-design-input"
          />
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs font-mono">Subiendo…</span></>
          ) : (
            <><Upload className="w-4 h-4" /><span className="text-xs font-mono uppercase">Subir diseño (PNG · JPG · PDF · 20 MB)</span></>
          )}
        </label>
      </div>
    </div>
  );
}
